import type { TrainingPlan, UserProfile } from '@shared/types';
import { useCallback, useEffect, useState } from 'react';
import {
  clearTrainingPlan,
  getStorageInfo,
  isStoredPlanCompatible,
  loadTrainingPlan,
  saveTrainingPlan,
  updateTrainingPlan,
} from '../services/trainingPlanStorage';
import { logger } from '../utils/logger';

/**
 * Hook for managing training plan persistence with localStorage
 *
 * Provides a clean interface for loading, saving, and updating
 * training plans with proper error handling and compatibility checks
 */
export interface UseTrainingPlanPersistenceResult {
  // State
  storedPlan: TrainingPlan | null;
  hasStoredPlan: boolean;
  isLoading: boolean;
  storageInfo: ReturnType<typeof getStorageInfo>;

  // Actions
  saveToStorage: (plan: TrainingPlan, profile: UserProfile) => Promise<boolean>;
  updateInStorage: (plan: TrainingPlan, profile: UserProfile) => Promise<boolean>;
  loadFromStorage: () => Promise<TrainingPlan | null>;
  clearStorage: () => void;
  checkCompatibility: (profile: UserProfile) => boolean;
  refreshStorageInfo: () => void;
}

export function useTrainingPlanPersistence(): UseTrainingPlanPersistenceResult {
  const [storedPlan, setStoredPlan] = useState<TrainingPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [storageInfo, setStorageInfo] = useState(() => getStorageInfo());

  // Load stored plan on mount
  useEffect(() => {
    const loadStoredPlan = async () => {
      try {
        setIsLoading(true);
        const plan = await loadTrainingPlan();
        setStoredPlan(plan);
      } catch (error) {
        logger.error('Failed to load stored training plan', { error });
        setStoredPlan(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredPlan();
  }, []);

  // Refresh storage info whenever stored plan changes
  useEffect(() => {
    setStorageInfo(getStorageInfo());
  }, [storedPlan]);

  const saveToStorage = useCallback(
    async (plan: TrainingPlan, profile: UserProfile): Promise<boolean> => {
      const success = await saveTrainingPlan(plan, profile);
      if (success) {
        setStoredPlan(plan);
        setStorageInfo(getStorageInfo());
      }
      return success;
    },
    []
  );

  const updateInStorage = useCallback(
    async (plan: TrainingPlan, profile: UserProfile): Promise<boolean> => {
      const success = await updateTrainingPlan(plan, profile);
      if (success) {
        setStoredPlan(plan);
        setStorageInfo(getStorageInfo());
      }
      return success;
    },
    []
  );

  const loadFromStorage = useCallback(async (): Promise<TrainingPlan | null> => {
    try {
      const plan = await loadTrainingPlan();
      setStoredPlan(plan);
      setStorageInfo(getStorageInfo());
      return plan;
    } catch (error) {
      logger.error('Failed to load from storage', { error });
      return null;
    }
  }, []);

  const clearStorage = useCallback((): void => {
    clearTrainingPlan();
    setStoredPlan(null);
    setStorageInfo(getStorageInfo());
  }, []);

  const checkCompatibility = useCallback((profile: UserProfile): boolean => {
    return isStoredPlanCompatible(profile);
  }, []);

  const refreshStorageInfo = useCallback((): void => {
    setStorageInfo(getStorageInfo());
  }, []);

  return {
    // State
    storedPlan,
    hasStoredPlan: !!storedPlan,
    isLoading,
    storageInfo,

    // Actions
    saveToStorage,
    updateInStorage,
    loadFromStorage,
    clearStorage,
    checkCompatibility,
    refreshStorageInfo,
  };
}
